import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { getAllRevisionsQuery } from './query.get-all.js';
import { createRevisionRequest } from './request.create.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function useCreateRevisionMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  return useMutation(createRevisionMutation(client, notifier, user));
}
const createRevisionMutation = (client, notifier, user) => {
  return {
    mutationKey: ["course-revisions/create", user?.id],
    mutationFn: async revision => await createRevisionRequest(user, revision),
    onMutate: async optimistic => {
      const previouslyActive = client.getQueryData(getAllRevisionsQuery.queryKey({
        user,
        courseId: optimistic.courseId
      }))?.find(it => it.isActive);
      client.setQueryData(getAllRevisionsQuery.queryKey({
        user,
        courseId: optimistic.courseId
      }), previous => [...(previous?.map(prev => ({
        ...prev,
        isActive: false
      })) ?? []), optimistic]);
      await client.cancelQueries({
        queryKey: getAllRevisionsQuery.queryKey({
          user,
          courseId: optimistic.courseId
        })
      });
      return {
        optimistic,
        previouslyActive
      };
    },
    onSuccess: (result, _, context) => {
      notifier.notify({
        id: "course-revisions.create",
        type: "success",
        body: /*#__PURE__*/jsxs("span", {
          children: ["Versionen er nu gemt med navnet", /*#__PURE__*/jsx("br", {}), /*#__PURE__*/jsxs("b", {
            children: ["\u2018", result.name, "\u2019"]
          }), "!"]
        })
      });
      client.setQueryData(getAllRevisionsQuery.queryKey({
        user,
        courseId: result.courseId
      }), previous => previous?.map(course => course.__clientId === context?.optimistic.__clientId ? result : course));
    },
    onError: (error, input, context) => {
      notifier.notify({
        id: "course-revisions.create.error",
        type: "error",
        body: "Versionen kunne ikke oprettes. Prøv igen om lidt."
      });
      client.setQueryData(getAllRevisionsQuery.queryKey({
        user,
        courseId: input.courseId
      }), previous => previous?.filter(revision => revision.__clientId !== context?.optimistic.__clientId).map(revision => revision.__clientId === context?.previouslyActive?.__clientId ? {
        ...revision,
        isActive: true
      } : revision));
    }
  };
};

export { useCreateRevisionMutation };
//# sourceMappingURL=mutation.create.js.map
